import { Component, useEffect, useRef, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { GenerationPDFnotes } from "./GenerationPDFnotes/GenerationPDFnotes";
import { showPreview } from "utils/common";
import { uploadEmrDocsRequest } from "api/emr";
import { queryKeys } from "queries/keys";
import { QueryError } from "components/Common/QueryState/QueryState";
import {
  DOC_LABELS,
  dismissFailedJob,
  enqueueFinalNotePdfPersist,
  failQueueHead,
  finishQueueHead,
  getQueueHead,
  isJobStale,
  pruneStaleJobs,
  setFailedJobToast,
  subscribeToQueue,
  takeFailedJob,
} from "./finalNotePdfQueue";

const FORM_KEYS = { note: "emr_note" };
const PDF_SCALE = 2;
const JOB_TIMEOUT_MS = 60000;
const SAVED_TOAST_MS = 4000;

function retry(jobId) {
  const job = takeFailedJob(jobId);
  if (!job) return;
  if (isJobStale(job)) {
    toast.dismiss(job.toastId);
    return;
  }
  enqueueFinalNotePdfPersist(job.payload, job.toastId);
}

// toast.update() does not start the auto-close timer on a toast created with autoClose: false.
function showSaved(job) {
  toast.update(job.toastId, {
    type: toast.TYPE.SUCCESS,
    render: `The ${DOC_LABELS[job.payload.docType]} PDF was saved.`,
    autoClose: false,
    closeOnClick: true,
  });
  setTimeout(() => toast.dismiss(job.toastId), SAVED_TOAST_MS);
}

// A slow upload can still land after its job timed out, so failed jobs count too.
function markSaved(job) {
  const done = finishQueueHead(job.id) ? job : takeFailedJob(job.id);
  if (done) showSaved(done);
}

function markFailed(job, error) {
  if (!failQueueHead(job.id)) return;
  console.error("[note-pdf] job failed:", job.payload.docType, error);

  const content = (
    <QueryError
      message={`The ${DOC_LABELS[job.payload.docType]} PDF could not be saved.`}
      onRetry={() => retry(job.id)}
    />
  );
  const options = {
    autoClose: false,
    closeOnClick: false,
    onClose: () => dismissFailedJob(job.id),
  };
  if (toast.isActive(job.toastId)) {
    toast.update(job.toastId, {
      ...options,
      type: toast.TYPE.ERROR,
      render: content,
    });
  } else {
    setFailedJobToast(job.id, toast.error(content, options));
  }
}

const ASSET_WAIT_MS = 3000;

const waitForAssets = (elem) =>
  Promise.race([
    loadAssets(elem),
    new Promise((resolve) => setTimeout(resolve, ASSET_WAIT_MS)),
  ]);

const loadAssets = (elem) =>
  Promise.all([
    document.fonts?.ready,
    ...Array.from(elem.querySelectorAll("img"))
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          }),
      ),
  ]);

const afterIdle = (fn) =>
  window.requestIdleCallback
    ? window.requestIdleCallback(fn, { timeout: 1000 })
    : setTimeout(fn, 50);

async function generateAndUploadPdf(job, container) {
  const { docType, requestUuid } = job.payload;
  const elem = container?.querySelector(`#pdf-prewie-${docType}`);
  if (!elem) throw new Error("note_pdf_dom_missing");

  await waitForAssets(elem);
  const blob = await showPreview(elem, docType, false, false, {
    scale: PDF_SCALE,
    resetScroll: false,
    imageFormat: "JPEG",
  });
  if (!blob) throw new Error("note_pdf_generation_failed");
  if (isJobStale(job)) throw new Error("note_pdf_session_changed");

  const body = new FormData();
  const formKey = FORM_KEYS[docType] ?? docType;
  body.append(formKey, blob, `${formKey}.pdf`);
  await uploadEmrDocsRequest(body, requestUuid, { useLoading: false });
}

function useRefreshAfterUpload() {
  const queryClient = useQueryClient();
  return ({ docType, requestUuid }) => {
    const docKeys = {
      note: queryKeys.emr.visitNotes(requestUuid),
      discharge: queryKeys.emr.discharge(requestUuid),
    };
    queryClient.invalidateQueries({ queryKey: docKeys[docType] });
    queryClient.invalidateQueries({
      queryKey: queryKeys.serviceRequests.detail(requestUuid),
    });
  };
}

class RenderErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    this.props.onError(error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function FinalNotePdfPersistHost({ pathname }) {
  const job = useSyncExternalStore(subscribeToQueue, getQueueHead);
  const refreshAfterUpload = useRefreshAfterUpload();
  const containerRef = useRef(null);
  const startedJobId = useRef(null);

  useEffect(() => {
    pruneStaleJobs();
  }, [job, pathname]);

  useEffect(() => {
    if (!job) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [job]);

  if (!job) return null;
  const { payload } = job;

  // GenerationPDFnotes re-fires this callback on re-render.
  const run = () => {
    if (startedJobId.current === job.id) return;
    startedJobId.current = job.id;

    afterIdle(() => {
      const timer = setTimeout(
        () => markFailed(job, new Error("note_pdf_timeout")),
        JOB_TIMEOUT_MS,
      );
      generateAndUploadPdf(job, containerRef.current)
        .then(() => {
          job.payload.onSaved?.();
          refreshAfterUpload(job.payload);
        })
        .then(() => markSaved(job))
        .catch((error) => markFailed(job, error))
        .finally(() => clearTimeout(timer));
    });
  };

  return createPortal(
    <div
      key={job.id}
      ref={containerRef}
      aria-hidden
      style={{
        position: "fixed",
        left: -10000,
        top: 0,
        width: 794,
        pointerEvents: "none",
      }}
    >
      <RenderErrorBoundary onError={(error) => markFailed(job, error)}>
        <GenerationPDFnotes
          notesView={[payload.docType]}
          {...payload.renderProps}
          callBackAfterRender={run}
        />
      </RenderErrorBoundary>
    </div>,
    document.body,
  );
}
